import { useEffect, useMemo, useState } from "react";
import { PlusCircleOutlined, MoreOutlined } from "@ant-design/icons";
import { Spin, Modal, Button, Dropdown } from "antd";
import { useNavigate } from "react-router-dom";
import TableComponent, { DataTypeProps } from "../../components/TableComponent";
import MainButtonComponent from "../../components/MainButtonComponent";
import SearchComponent from "../../components/SearchComponent";
import { createColumn } from "../../utils/tableUtils";
import {
	HeaderComponent,
	Container,
	ContainerHeader,
	FiltersContainer,
	ActionButtonOverlay,
	StatusCell,
} from "../../components/styled/common";
import { useVolunteersGalleryContext } from "../../context/VolunteersGalleryContext/VolunteersGalleryContext";
import {
	getVolunteersGalleryEditPath,
	VOLUNTEERS_GALLERY_LIST_PATH,
	volunteersGalleryContextTypeLabels,
	VolunteersGalleryListItem,
} from "../../context/VolunteersGalleryContext/constants";

// The table shows, sorts and searches the place of the gallery (the home page or the project's name).
type VolunteersGalleryRow = VolunteersGalleryListItem & { name: string; placeType: string };

const VolunteersGalleryList: React.FC = () => {
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [currentItem, setCurrentItem] = useState<VolunteersGalleryRow | null>(null);
	const {
		volunteersGalleryList,
		clearVolunteersGalleryList,
		clearVolunteersGalleryFormData,
		getVolunteersGalleryList,
		removeVolunteersGallery,
		getGalleryName,
		operationStatus,
	} = useVolunteersGalleryContext();

	const [searchText, setSearchText] = useState("");

	const navigate = useNavigate();

	const rows = useMemo<VolunteersGalleryRow[]>(
		() => volunteersGalleryList.map(item => ({
			...item,
			name: getGalleryName(item),
			placeType: volunteersGalleryContextTypeLabels[item.contextType] ?? "",
		})),
		[volunteersGalleryList, getGalleryName]
	);

	const tableData = useMemo(() => {
		const search = searchText.trim().toLocaleLowerCase("tr-TR");
		return search ? rows.filter(x => x.name.toLocaleLowerCase("tr-TR").includes(search)) : rows;
	}, [rows, searchText]);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";

	useEffect(() => {
		clearVolunteersGalleryList();
		clearVolunteersGalleryFormData();
		getVolunteersGalleryList();

		return () => {
			clearVolunteersGalleryList();
		};
	}, [clearVolunteersGalleryList, clearVolunteersGalleryFormData, getVolunteersGalleryList]);

	// The count, status and action columns keep the width their headers need; the name shares the rest.
	const columns = useMemo(() => [
		createColumn({
			title: "Galeri",
			dataIndex: "name",
			sorterType: "string",
		}),
		createColumn({
			title: "Galeri Yeri",
			dataIndex: "placeType",
			width: 160,
			sorterType: "string",
		}),
		createColumn({
			title: "Öğe Sayısı",
			dataIndex: "itemCount",
			width: 140,
			sorterType: "numeric",
		}),
		createColumn({
			title: "Aktiflik Durumu",
			dataIndex: "isActive",
			width: 160,
			sorterType: "boolean",
			render: (text, record) => (
				<StatusCell $bool={record.isActive}>{record.isActive ? "Aktif" : "Pasif"}</StatusCell>
			),
		}),
		createColumn({
			title: "",
			dataIndex: "action",
			width: 50,
			render: (text, record) => {
				const item = record as VolunteersGalleryRow;
				const menuItems = [
					{
						key: "edit",
						label: "Güncelle",
						onClick: () => navigate(getVolunteersGalleryEditPath(item.id)),
					},
					{
						key: "delete",
						label: "Sil",
						onClick: () => {
							setCurrentItem(item);
							setIsModalVisible(true);
						},
					},
				];

				return (
					<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
						<MoreOutlined />
					</Dropdown>
				);
			},
		}),
	], [navigate]);

	return (
		<div>
			<HeaderComponent>Galeri</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>
					<ContainerHeader style={{ justifyContent: "space-between" }}>
						<FiltersContainer>
							<SearchComponent
								placeholder="Galeri Ara"
								style={{ width: "16vw" }}
								value={searchText}
								onChange={setSearchText}
							/>
						</FiltersContainer>
						<ActionButtonOverlay>
							<MainButtonComponent
								style={{ marginRight: "15px" }}
								icon={<PlusCircleOutlined />}
								onClick={() => navigate(`${VOLUNTEERS_GALLERY_LIST_PATH}/add`)}
							>
								Yeni Ekle
							</MainButtonComponent>
						</ActionButtonOverlay>
					</ContainerHeader>
				</Spin>
				{!isFetching && (
					<TableComponent
						columns={columns}
						dataSource={tableData as unknown as DataTypeProps[]}
						rowKey="id"
					/>
				)}
			</Container>
			<Modal
				title="Galeriyi Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button
						loading={isDeleting}
						key="submit"
						type="primary"
						danger
						onClick={async () => {
							if (currentItem) {
								await removeVolunteersGallery(currentItem.id);
							}
							setIsModalVisible(false);
						}}
					>
						Evet
					</Button>,
				]}
			>
				<p>
					<strong>{`"${currentItem?.name ?? ""}"`}</strong> galerisini ve içindeki {currentItem?.itemCount ?? 0} görsel ve videoyu silmek istediğinizden emin misiniz?
				</p>
			</Modal>
		</div>
	);
};

export default VolunteersGalleryList;
